import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  // undefined = validando, true = sessão de recuperação ok, false = sem sessão
  const [ready, setReady] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    // O link do e-mail traz o token no hash; o client da auth processa
    // automaticamente e dispara PASSWORD_RECOVERY com a sessão temporária.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        setReady(true);
      }
    });

    // Cobre o caso de a sessão já existir (refresh, navegação interna).
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setReady(true);
      } else {
        // Pequena janela para o client processar o hash da URL antes de
        // decidirmos que não há sessão.
        setTimeout(() => {
          setReady((prev) => (prev === undefined ? false : prev));
        }, 1500);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A senha deve ter ao menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não conferem");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Senha redefinida! Entre com sua nova senha.");
      await supabase.auth.signOut();
      navigate({ to: "/auth" });
    } catch (err: any) {
      toast.error(err.message || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <Trophy className="h-7 w-7 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold">FutBux</h1>
          <p className="text-sm text-muted-foreground mt-1">Redefinir senha</p>
        </div>
        <div className="bg-card rounded-xl shadow-sm border p-5">
          {ready === undefined && (
            <p className="text-sm text-muted-foreground text-center py-6">
              Validando link...
            </p>
          )}

          {ready === false && (
            <div className="space-y-4 text-center py-2">
              <p className="text-sm text-foreground">
                Link inválido ou expirado.
              </p>
              <p className="text-sm text-muted-foreground">
                Abra o link mais recente enviado para o seu e-mail, ou peça um
                novo em "Esqueci minha senha".
              </p>
              <Button
                type="button"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => navigate({ to: "/auth" })}
              >
                Voltar ao login
              </Button>
            </div>
          )}

          {ready === true && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nova senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirme a nova senha</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={loading}
              >
                {loading ? "Salvando..." : "Redefinir senha"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
